import { useState } from 'react';
import { User } from '../../types/user';
import { Dialog } from '@headlessui/react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { UserIcon, PlusIcon } from '@heroicons/react/24/outline';
import { addUserToProperty, removeUserFromProperty } from '../../services/properties';
import toast from 'react-hot-toast';

interface PropertyUserManagerProps {
  propertyId: string;
  propertyUsers: User[];
  availableUsers: User[];
  onUsersChange: () => void;
}

export const PropertyUserManager = ({ 
  propertyId, 
  propertyUsers, 
  availableUsers,
  onUsersChange 
}: PropertyUserManagerProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddUser = async () => {
    if (!selectedUserId) {
      toast.error('Por favor, selecciona un usuario');
      return;
    }

    setLoading(true);
    try {
      await addUserToProperty(selectedUserId, propertyId);
      toast.success('Usuario añadido correctamente');
      setShowModal(false);
      setSelectedUserId('');
      onUsersChange();
    } catch (error) {
      console.error('Error al añadir usuario:', error);
      toast.error('Error al añadir usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este usuario de la propiedad?')) {
      return;
    }

    try {
      await removeUserFromProperty(userId);
      toast.success('Usuario eliminado correctamente');
      onUsersChange();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      toast.error('Error al eliminar usuario');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Usuarios Autorizados</h3>
        <Button
          onClick={() => setShowModal(true)}
          className="flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Añadir Usuario
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {propertyUsers.map((user) => (
          <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
            <div className="flex items-center space-x-3">
              <UserIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium">{user.name}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>
            <button
              onClick={() => handleRemoveUser(user.id)}
              className="text-red-600 hover:text-red-800"
            >
              <span className="sr-only">Eliminar</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

          <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <Dialog.Title className="text-lg font-semibold mb-4">
              Añadir Usuario a la Propiedad
            </Dialog.Title>

            <Select
              label="Usuario"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              options={availableUsers.map(user => ({
                value: user.id,
                label: `${user.name} (${user.email})`
              }))}
            />

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddUser}
                loading={loading}
                disabled={!selectedUserId}
              >
                Añadir
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};